/**
 * Outlook Email Draft Service
 * 
 * Creates email drafts via Microsoft Graph API integration.
 * Requires user to have Outlook connected in their settings.
 */

import { getAuthCookie } from '@quotech/auth';

import { API_BASE } from '@quotech/api';
import { buildOrganisationHeader } from '../../organisation';

interface OutlookConnectionStatus {
  connected: boolean;
  email: string | null;
  lastSync: string | null;
  connectUrl?: string;
}

interface CreateDraftRequest {
  subject: string;
  bodyHtml: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  importance?: 'low' | 'normal' | 'high';
  attachments?: string[]; // FileRecord IDs from QDrive
}

interface DraftResponse {
  draftId: string;
  webLink: string;
  subject: string;
}

/**
 * Check if user has Outlook connected
 */
export async function checkOutlookConnection(): Promise<OutlookConnectionStatus> {
  try {
    const token = getAuthCookie();
    const response = await fetch(`${API_BASE}/api/ai/outlook/status/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...buildOrganisationHeader(),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        return { connected: false, email: null, lastSync: null, connectUrl: '/settings?connect_outlook=true' };
      }
      throw new Error(`Failed to check Outlook status: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      connected: data.connected,
      email: data.email,
      lastSync: data.last_sync,
      connectUrl: data.connect_url,
    };
  } catch (error) {
    console.error('[OutlookService] Error checking connection:', error);
    return { connected: false, email: null, lastSync: null };
  }
}

/**
 * Create an Outlook email draft
 */
export async function createOutlookDraft(request: CreateDraftRequest): Promise<DraftResponse> {
  const token = getAuthCookie();
  const response = await fetch(`${API_BASE}/api/ai/outlook/draft/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      ...buildOrganisationHeader(),
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      subject: request.subject,
      body_html: request.bodyHtml,
      to: request.to || [],
      cc: request.cc || [],
      bcc: request.bcc || [],
      importance: request.importance || 'normal',
      attachments: request.attachments || [],
    }),
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create draft: ${response.status}`);
  }
  
  const data = await response.json();
  return {
    draftId: data.draft_id,
    webLink: data.web_link,
    subject: data.subject,
  };
}

/**
 * Open the created draft in Outlook web
 */
export function openDraftInOutlook(webLink: string): void {
  window.open(webLink, '_blank', 'noopener,noreferrer');
}

/**
 * Generate email draft from AI analysis
 * 
 * This is a convenience function that generates email content
 * based on context and creates the draft.
 */
export async function generateAndCreateDraft(params: {
  type: 'followup' | 'invoice_reminder' | 'quote_response' | 'status_update' | 'custom';
  context: {
    jobId?: number;
    jobName?: string;
    customerName?: string;
    customerEmail?: string;
    amount?: number;
    customPrompt?: string;
  };
}): Promise<DraftResponse | null> {
  // First, generate the email content via AI
  const token = getAuthCookie();
  try {
    const generateResponse = await fetch(`${API_BASE}/api/ai/quote/compose-email/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...buildOrganisationHeader(),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        email_type: params.type,
        job_id: params.context.jobId,
        job_name: params.context.jobName,
        customer_name: params.context.customerName,
        amount: params.context.amount,
        custom_prompt: params.context.customPrompt,
      }),
    });
    
    if (!generateResponse.ok) {
      console.error('[OutlookService] Failed to generate email content');
      return null;
    }
    
    const generated = await generateResponse.json();
    
    // Now create the draft
    return await createOutlookDraft({
      subject: generated.subject || `Re: ${params.context.jobName || 'Your Inquiry'}`,
      bodyHtml: generated.body_html || generated.body || '',
      to: params.context.customerEmail ? [params.context.customerEmail] : [],
      importance: 'normal',
    });
    
  } catch (error) {
    console.error('[OutlookService] Error generating and creating draft:', error);
    return null;
  }
}

